'use client'

import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Section } from '@/components/ui/section'
import { TypographyH2, TypographyH3, TypographyP } from '@/components/ui/typography'

import { cn } from '@/lib/utils'
import { Container } from '@/components/ui/container'
import { Col } from '@/components/ui/col'
import { Measure } from '@/components/ui/measure'

export type HomeMarketingLayerId = 'l05' | 'l04' | 'l03' | 'l02' | 'l01'

export type HomeMarketingLayersCopy = {
  title: string
  desc: string
  rows: {
    id: HomeMarketingLayerId
    label: string
    title: string
    desc: string
  }[]
  frag: {
    fragL05Endpoint: string
    fragL04Title: string
    fragL04Live: string
    fragL03Title: string
    fragL02Title: string
    fragL02ItemName: string
    fragL02ItemValue: string
    fragL02PriceLabel: string
    fragL02PriceValue: string
    deployBtn: string
    fragL01Table: string
  }
}

export function HomeMarketingLayers({ copy }: { copy: HomeMarketingLayersCopy }) {
  const [active, setActive] = useState<HomeMarketingLayerId>('l05')

  return (
    <Section tone="charcoal900" spacing="py32" border="bCharcoal800">
      <Container size="site">
        <Measure size="2xl" centered={false} className="mb-16">
          <TypographyH2 tone="white" className="mb-4">
            {copy.title}
          </TypographyH2>
          <TypographyP size="lead" tone="muted" className="font-light">
            {copy.desc}
          </TypographyP>
        </Measure>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <Col className="relative z-10 border border-charcoal-700 bg-charcoal-900 shadow-2xl lg:col-span-7">
            {copy.rows.map((layer, i) => (
              <div
                key={layer.id}
                role="button"
                tabIndex={0}
                onMouseEnter={() => setActive(layer.id)}
                onFocus={() => setActive(layer.id)}
                className={cn(
                  'group relative flex cursor-pointer items-center overflow-hidden border-charcoal-700 p-8 transition-colors',
                  i < copy.rows.length - 1 && 'border-b',
                  active === layer.id ? 'bg-charcoal-800' : 'hover:bg-charcoal-800',
                )}
              >
                <div
                  className={cn(
                    'absolute bottom-0 left-0 top-0 w-1 bg-brand-red transition-opacity',
                    active === layer.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                  )}
                />
                <span
                  className={cn(
                    'w-12 text-fs-sm font-bold tracking-widest',
                    active === layer.id
                      ? 'text-brand-red'
                      : 'text-charcoal-500 group-hover:text-brand-red',
                  )}
                >
                  {layer.label}
                </span>
                <div>
                  <TypographyH3
                    scale="sm"
                    tone="inherit"
                    className="mb-1 text-base text-gray-100 group-hover:text-white"
                  >
                    {layer.title}
                  </TypographyH3>
                  <TypographyP
                    size="sm"
                    tone="charcoal500"
                    className="group-hover:text-charcoal-300 font-light"
                  >
                    {layer.desc}
                  </TypographyP>
                </div>
              </div>
            ))}
          </Col>

          <div className="relative hidden min-h-[500px] lg:col-span-5 lg:block">
            <div className="sticky top-32 flex h-full min-h-[500px] w-full items-center justify-center overflow-hidden border border-charcoal-700 bg-charcoal-800 p-6 shadow-2xl">
              <FragmentPanel active={active} copy={copy.frag} />
            </div>
          </div>
        </div>
      </Container>
    </Section>
  )
}

function FragmentPanel({
  active,
  copy,
}: {
  active: HomeMarketingLayerId
  copy: HomeMarketingLayersCopy['frag']
}) {
  return (
    <>
      <Col
        className={cn(
          'ui-fragment absolute inset-0 z-10 bg-charcoal-900 p-8 transition-colors',
          active === 'l05'
            ? 'opacity-100'
            : 'pointer-events-none z-0 opacity-0 translate-y-4',
        )}
      >
        <div className="mb-4 border-b border-charcoal-700 pb-2 font-mono text-fs-sm text-charcoal-500">
          {copy.fragL05Endpoint}
        </div>
        <div className="font-mono text-xs leading-relaxed text-charcoal-300">
          <span className="text-brand-red">const</span> payload = {'{'}
          <br />
          &nbsp;&nbsp;&quot;store_id&quot;: <span className="text-green-400">&quot;VN-SGN-045&quot;</span>,
          <br />
          &nbsp;&nbsp;&quot;timestamp&quot;: <span className="text-blue-400">1712543200</span>,<br />
          &nbsp;&nbsp;&quot;items&quot;: [
          <br />
          &nbsp;&nbsp;&nbsp;&nbsp;{'{'} &quot;id&quot;:{' '}
          <span className="text-green-400">&quot;ITM-992&quot;</span>, &quot;qty&quot;:{' '}
          <span className="text-blue-400">2</span> {'}'}
          <br />
          &nbsp;&nbsp;]
          <br />
          {'};'}
          <br />
          <br />
          <span className="text-charcoal-500">{'// Enterprise Service Bus routing...'}</span>
          <br />
          <span className="text-brand-red">await</span> ESB.dispatch(payload);
        </div>
      </Col>

      <Col
        className={cn(
          'ui-fragment absolute inset-0 z-10 bg-white p-8 transition-colors',
          active === 'l04'
            ? 'opacity-100'
            : 'pointer-events-none z-0 opacity-0 translate-y-4',
        )}
      >
        <div className="mb-6 flex items-center justify-between">
          <span className="text-xs font-bold uppercase text-charcoal-900">{copy.fragL04Title}</span>
          <span className="rounded bg-gray-100 px-2 py-1 text-fs-sm text-charcoal-600">
            {copy.fragL04Live}
          </span>
        </div>
        <div className="flex flex-1 items-end gap-2 border-b border-gray-200 pb-2">
          <div className="h-[30%] w-1/4 bg-charcoal-200 transition-colors hover:bg-brand-red" />
          <div className="h-[50%] w-1/4 bg-charcoal-300 transition-colors hover:bg-brand-red" />
          <div className="h-[80%] w-1/4 bg-charcoal-600 transition-colors hover:bg-brand-red" />
          <div className="h-full w-1/4 bg-charcoal-900 transition-colors hover:bg-brand-red" />
        </div>
        <div className="mt-2 text-right font-mono text-fs-sm text-charcoal-700">Dcorp OLAP Engine</div>
      </Col>

      <Col
        className={cn(
          'ui-fragment absolute inset-0 z-10 bg-gray-50 p-8 transition-colors',
          active === 'l03'
            ? 'opacity-100'
            : 'pointer-events-none z-0 opacity-0 translate-y-4',
        )}
      >
        <div className="mb-4 border-b border-gray-200 pb-2 text-xs font-bold uppercase text-charcoal-900">
          {copy.fragL03Title}
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between border-l-4 border-brand-red bg-white p-3 shadow-sm">
            <span className="text-sm font-bold">#ORD-9912</span>
            <span className="text-fs-sm bg-green-100 px-2 py-1 text-green-700">GrabFood</span>
          </div>
          <div className="flex items-center justify-between border-l-4 border-charcoal-800 bg-white p-3 shadow-sm">
            <span className="text-sm font-bold">#ORD-9913</span>
            <span className="text-fs-sm bg-blue-100 px-2 py-1 text-blue-700">Brand App</span>
          </div>
        </div>
      </Col>

      <Col
        className={cn(
          'ui-fragment absolute inset-0 z-10 flex-1 bg-white p-8 transition-colors',
          active === 'l02'
            ? 'opacity-100'
            : 'pointer-events-none z-0 opacity-0 translate-y-4',
        )}
      >
        <div className="mb-4 border-b border-gray-200 pb-2 text-xs font-bold uppercase text-charcoal-900">
          {copy.fragL02Title}
        </div>
        <Col className="flex-1 space-y-4">
          <div>
            <label className="mb-1 block text-fs-sm uppercase text-charcoal-700">{copy.fragL02ItemName}</label>
            <div className="border-b border-charcoal-900 pb-1 text-sm font-bold">{copy.fragL02ItemValue}</div>
          </div>
          <div>
            <label className="mb-1 block text-fs-sm uppercase text-charcoal-700">{copy.fragL02PriceLabel}</label>
            <div className="border-b border-gray-300 pb-1 text-sm text-charcoal-600">{copy.fragL02PriceValue}</div>
          </div>
          <div className="pt-4">
            <Button type="button" variant="primary" size="xs" className="w-full">
              {copy.deployBtn}
            </Button>
          </div>
        </Col>
      </Col>

      <div
        className={cn(
          'ui-fragment absolute inset-0 z-10 flex items-center justify-center bg-gray-200 p-8 transition-colors',
          active === 'l01'
            ? 'opacity-100'
            : 'pointer-events-none z-0 opacity-0 translate-y-4',
        )}
      >
        <div className="w-full border-t-4 border-brand-red bg-white p-5 shadow-xl">
          <div className="mb-3 flex justify-between border-b border-gray-200 pb-2">
            <span className="text-xl font-bold text-charcoal-900">{copy.fragL01Table}</span>
            <span className="font-mono text-lg text-brand-red animate-pulse">14:02</span>
          </div>
          <ul className="space-y-2 font-mono text-sm text-charcoal-800">
            <li className="font-bold">1x Ribeye Steak (Med-Rare)</li>
            <li className="pl-4 text-brand-red">- No Garlic</li>
            <li className="font-bold">2x Truffle Fries</li>
          </ul>
        </div>
      </div>
    </>
  )
}
